import { useEffect } from 'react'
import Viewer3D from '@viewer/canvas'
import Sidebar from '@core/layout/sidebar'
import RightSidebar from '@core/layout/right-sidebar'
import DetachedRightSidebar from '@core/layout/right-sidebar/Detached'
import BrowserWindowSidebar from '@core/layout/right-sidebar/BrowserWindow'
import ViewpointsPanelFixed from '@core/layout/viewpoints-panel'
import DetachedViewpointsPanel from '@core/layout/viewpoints-panel/Detached'
import BrowserWindowViewpoints from '@core/layout/viewpoints-panel/BrowserWindow'
import MobileDrawer from '@core/layout/MobileDrawer'
import { useMobileUi } from '@core/layout/mobileUiStore'
import { useIsMobile } from '@core/useIsMobile'
import { useAppStore } from '@core/store'
import OverlayEffectsHost from '@viewer/lib/OverlayEffectsHost'

interface ViewerPageProps {
  /** Set false when the viewer is hidden behind a fullscreen module
   * panel. We use display:none rather than unmount so the Three.js
   * scene, WebGL context, and loaded models survive across navigation
   * remounting tears everything down. */
  visible?: boolean
}

/**
 * Self-contained viewer slot: own outer flex container so it can sit
 * next to (or above) a module pane in WorkspaceLayout without leaking
 * its inner flex layout into the surrounding row.
 *
 * On phones (< md) it swaps to the dedicated mobile layout: the canvas
 * fills the screen and the model list / properties live in slide-in
 * drawers toggled from the bottom MobileBar (see ViewerShell /
 * WorkspaceLayout). The desktop three-column layout is untouched.
 *
 * UM só return, e o `<main><Viewer3D/></main>` sempre no mesmo lugar da
 * árvore, com o mesmo pai. Havia dois componentes (desktop e telemóvel),
 * e passar a fronteira do `useIsMobile` (janela abaixo de 768 px de
 * largura ou 450 px de altura: DevTools ancoradas em baixo, janela num
 * quarto do ecrã, tablet pequeno a rodar) trocava o tipo do elemento. O
 * React desmontava o Viewer3D, o contexto WebGL caía e os modelos saíam
 * da cena. Só muda o que está à volta: as barras laterais no desktop,
 * as gavetas no telemóvel.
 */
function ViewerPage({ visible = true }: ViewerPageProps) {
  const mode = useAppStore((s) => s.rightSidebarMode)
  const isMobile = useIsMobile()
  const openDrawer = useMobileUi((s) => s.openDrawer)
  const closeDrawer = useMobileUi((s) => s.closeDrawer)
  const setLeftCollapsed = useAppStore((s) => s.setLeftSidebarCollapsed)
  const setRightCollapsed = useAppStore((s) => s.setRightSidebarCollapsed)

  // The drawers host the full sidebars, so make sure they aren't in their
  // collapsed-strip state when shown.
  useEffect(() => {
    if (!isMobile) return
    if (openDrawer === 'models') setLeftCollapsed(false)
    if (openDrawer === 'properties') setRightCollapsed(false)
  }, [isMobile, openDrawer, setLeftCollapsed, setRightCollapsed])

  return (
    <div
      // overflow-hidden: com um módulo acoplado em baixo e alto, a barra
      // lateral e o cubo de vistas transbordavam por cima dele (Rui,
      // 16/10/2026).
      className={`${isMobile ? 'relative ' : ''}flex min-h-0 min-w-0 flex-1 overflow-hidden`}
      style={visible ? undefined : { display: 'none' }}
    >
      <OverlayEffectsHost />
      {!isMobile && <Sidebar />}
      <main className="relative min-h-0 min-w-0 flex-1 overflow-hidden">
        <Viewer3D />
      </main>
      <ViewpointsPanelFixed />
      {!isMobile && mode === 'fixed' && <RightSidebar />}
      {!isMobile && <DetachedRightSidebar />}
      {!isMobile && <BrowserWindowSidebar />}
      {!isMobile && <DetachedViewpointsPanel />}
      {!isMobile && <BrowserWindowViewpoints />}
      {/* Telemóvel: a lista de modelos e as propriedades em gavetas,
          abertas pela barra de baixo (MobileBar). As gavetas reutilizam
          os mesmos Sidebar e RightSidebar; o MobileDrawer só lhes tira a
          largura de doca do desktop. */}
      {isMobile && openDrawer === 'models' && (
        <MobileDrawer
          side="left"
          title="Modelos · Estrutura"
          onClose={closeDrawer}
        >
          <Sidebar />
        </MobileDrawer>
      )}
      {isMobile && openDrawer === 'properties' && (
        <MobileDrawer side="right" title="Propriedades" onClose={closeDrawer}>
          <RightSidebar />
        </MobileDrawer>
      )}
    </div>
  )
}

export default ViewerPage
